/**
 * The geometry of dragging a card. Every function here is a worklet so the UI thread can call it
 * per frame; none touches React state. The thresholds are the tunable half of the plan — initial
 * values, to be adjusted against real rendered layouts.
 */

/** Short elastic travel after the corner extends 9pt beyond the screen edge. */
export const HORIZONTAL_DRAG_OVERSHOOT = 36;

/** Immediately reduce motion to one fifth of finger travel past the threshold. */
const HORIZONTAL_DRAG_RESISTANCE = 0.2;

/** The upper outside corner meets the screen edge; the rest stays inside the viewport. */
export function loadingCardParkTransform(
  direction: "left" | "right",
  width: number,
  height: number,
  clearance = 9,
) {
  "worklet";
  const sign = direction === "left" ? -1 : 1;
  const angle = Math.min(Math.PI / 180, Math.atan2(clearance, Math.max(height, 1)));
  const cornerExtension = (width * Math.cos(angle) + height * Math.sin(angle) - width) / 2;
  return { tilt: (sign * angle * 180) / Math.PI, x: sign * (clearance - cornerExtension), y: 0 };
}

/** Degrees of tilt per card-width of visual travel, independent of the loading park pose. */
export const HORIZONTAL_TILT_DEGREES = 18;

export type HorizontalDragDirection = "left" | "right";

/** Which way a horizontal displacement points, or `null` at rest. */
export function horizontalDragDirection(translationX: number): HorizontalDragDirection | null {
  "worklet";
  if (translationX === 0) {
    return null;
  }
  return translationX < 0 ? "left" : "right";
}

/** The 9pt overflow threshold is shared by friction, release and the action-button cue. */
export function isHorizontalDragCommitted({
  freeDistance,
  translationX,
}: {
  freeDistance: number;
  translationX: number;
}): boolean {
  "worklet";
  return Math.abs(translationX) > freeDistance;
}

/** A faint anticipation before the corner passes the 9pt overflow threshold, then a definite pressed state. */
export function horizontalSwipePressProgress(distance: number, freeDistance: number): number {
  "worklet";
  if (distance <= 0) {
    return 0;
  }
  if (isHorizontalDragCommitted({ freeDistance, translationX: distance })) {
    return 1;
  }
  return 0.08 * Math.pow(distance / Math.max(freeDistance, 0.001), 3);
}

/**
 * Follow the finger through the free range, then exponentially reduce further travel.
 * Position stays continuous, but the slope drops to 0.2 immediately and tends to zero at `maxDistance`.
 */
export function clampDragWithFriction({
  freeDistance,
  maxDistance,
  translation,
}: {
  freeDistance: number;
  maxDistance: number;
  translation: number;
}): number {
  "worklet";
  const distance = Math.abs(translation);
  if (distance <= freeDistance) {
    return translation;
  }
  const overshoot = distance - freeDistance;
  const room = Math.max(0, maxDistance - freeDistance);
  const resisted =
    room === 0 ? 0 : room * (1 - Math.exp((-overshoot * HORIZONTAL_DRAG_RESISTANCE) / room));
  return Math.sign(translation) * (freeDistance + resisted);
}

/** Recover the finger-space origin when grabbing a card during its return spring. */
export function rawDragForVisualOffset({
  freeDistance,
  maxDistance,
  translation,
}: {
  freeDistance: number;
  maxDistance: number;
  translation: number;
}): number {
  "worklet";
  const distance = Math.abs(translation);
  const room = Math.max(0, maxDistance - freeDistance);
  if (room === 0 || distance <= freeDistance) {
    return translation;
  }
  // The asymptote can round to maxDistance; keep the inverse finite.
  const fraction = Math.min((distance - freeDistance) / room, 1 - 1e-7);
  return (
    Math.sign(translation) *
    (freeDistance - (room / HORIZONTAL_DRAG_RESISTANCE) * Math.log(1 - fraction))
  );
}

/** Directional tilt proportional to visual travel, so rotation shares the drag's resistance. */
export function tiltForHorizontalOffset(
  translationX: number,
  cardWidth: number,
  maxDegrees?: number,
): number {
  "worklet";
  // Worklet closure capture reads module constants from the body, not parameter defaults.
  const cap = maxDegrees ?? HORIZONTAL_TILT_DEGREES;
  if (cardWidth <= 0) {
    return 0;
  }
  const fraction = Math.max(-1, Math.min(1, translationX / cardWidth));
  return fraction * cap;
}
/**
 * Precompute translation where the leading corner extends 9pt beyond the screen edge. The rotated
 * rectangle's half-width is (width * cos(angle) + height * sin(angle)) / 2. This uses settled
 * card dimensions once at gesture start; no measuring transformed layout during the drag.
 */
export function horizontalDragFreeDistance(width: number, height: number, screenInset = 9): number {
  "worklet";
  const clearance = Math.max(0, screenInset) + 9;
  let low = 0;
  let high = clearance;
  // Solve with the same tilt law used by the card; tall cards reach the edge sooner.
  for (let step = 0; step < 20; step += 1) {
    const x = (low + high) / 2;
    const angle = (tiltForHorizontalOffset(x, width) * Math.PI) / 180;
    const cornerExtension = (width * Math.cos(angle) + height * Math.sin(angle) - width) / 2;
    if (x + cornerExtension < clearance) {
      low = x;
    } else {
      high = x;
    }
  }
  return (low + high) / 2;
}

/**
 * Where a promoted card was when its exit started, and where it flies to. The successor took the
 * deck's shared motion at that moment, so the departing card is drawn from this pose instead.
 */
export type LearnCardDeparturePose = {
  presentation: string;
  tilt: number;
  toX: number;
  toY: number;
  x: number;
  y: number;
};

/**
 * A card sent off on the UI thread at the moment it was released, ahead of its logical exit: the
 * write still runs, but nothing on screen waits for it. A card is only ever released onto a next
 * flashcard that is ready to be shown; until then it stalls (`LearnCardStalledRelease`).
 */
export type LearnCardRelease = {
  /** The released presentation. React keeps it current until its exit starts. */
  from: string;
  /** The next flashcard, uncovered in its place and taking touches. */
  to: string;
};

/**
 * A committing release whose next flashcard has not finished preloading: the card waits at the
 * snap point (its parked edge, `loadingCardParkTransform`, or square just above rest for an upward
 * exit) and flies off in `direction` the moment that flashcard is ready. Nothing is uncovered
 * before then, so the card underneath is never shown half-loaded or replaced.
 */
export type LearnCardStalledRelease = {
  direction: "left" | "right" | "up";
  presentation: string;
};

/**
 * What a committing release does with the current card:
 *
 *   * `fly` — leaves at once (`LearnCardRelease`), uncovering the next flashcard, or stalls at the
 *     snap point until that flashcard is ready (`LearnCardStalledRelease`);
 *   * `park` — waits at the edge until its exit can start (the queue ends: the deck is left);
 *   * `return` — springs home; the release asks the card for something other than leaving (a
 *     lesson with an unviewed reading turns to that reading).
 */
export type LearnCardReleaseMode = "fly" | "park" | "return";

/**
 * Visual ownership never depends on React's current/successor role. During promotion either
 * thread may run first: prepared cards stay at rest; a retired card cannot consume reset motion.
 * A departing card (promoted past as its exit started) is drawn above everything from its own
 * pose, along `departureProgress`, until the current card's ownership says otherwise.
 *
 * With `pagedSuccessorOffset`, the current card pages on to its successor rather than uncovering
 * it: the successor waits that far to its right, beside it, and follows its drag.
 */
export function learnCardMotionStyle({
  departure = null,
  departureProgress = 0,
  motionPresentation,
  pagedSuccessorOffset = null,
  presentationKey,
  successorPresentation,
  tilt,
  x,
  y,
}: {
  departure?: LearnCardDeparturePose | null;
  departureProgress?: number;
  motionPresentation: { current: string; dismissed: string | null };
  /** Where the successor waits from its rest position, when the current card pages (a slide). */
  pagedSuccessorOffset?: number | null;
  presentationKey: string;
  successorPresentation: string | null;
  tilt: number;
  x: number;
  y: number;
}) {
  "worklet";
  if (
    motionPresentation.current !== presentationKey &&
    departure?.presentation === presentationKey
  ) {
    return {
      opacity: 1,
      transform: [
        { translateX: departure.x + (departure.toX - departure.x) * departureProgress },
        { translateY: departure.y + (departure.toY - departure.y) * departureProgress },
        { rotateZ: `${departure.tilt}deg` },
      ],
      zIndex: 3,
    };
  }
  if (motionPresentation.current !== presentationKey) {
    const isUnderlay = successorPresentation === presentationKey;
    // The third prepared front must never be exposed during a native child reorder or
    // promotion. Visibility and stacking follow the same UI-thread presentation ownership.
    // A history restore can make a previously dismissed presentation the underlay again.
    // Its current ownership takes precedence over the stale dismissal marker.
    return {
      opacity: isUnderlay ? 1 : 0,
      transform:
        isUnderlay && pagedSuccessorOffset !== null
          ? [{ translateX: x + pagedSuccessorOffset }]
          : [],
      zIndex: isUnderlay ? 1 : 0,
    };
  }
  return {
    opacity: 1,
    transform: [{ translateX: x }, { translateY: y }, { rotateZ: `${tilt}deg` }],
    zIndex: 2,
  };
}

/**
 * The waiting pose for an `up` exit: square, untilted, lifted just enough to read as "leaving".
 * The horizontal park leans the card toward its exit edge, which here would imply a left/right
 * choice the learner never made — and would leak which rating is being written.
 */
export function upwardCardParkTransform(): { tilt: number; x: number; y: number } {
  "worklet";
  return { tilt: 0, x: 0, y: -9 };
}
